// Phase A — Welfare masters API client.
// Colonies, Rooms, Beds, Canteens, Canteen Coupon Rules, Ration Rules.
import apiClient from '@/shared/api/client'

// ─── Types ────────────────────────────────────────────────────────────

export type OwnershipType = 'COMPANY_OWNED' | 'RENTED' | 'THIRD_PARTY'
export type RoomType =
  | 'WORKER_ROOM' | 'STAFF_ROOM' | 'FAMILY_QUARTER'
  | 'DORMITORY' | 'GUEST_ROOM'
export type RoomStatus =
  | 'AVAILABLE' | 'OCCUPIED' | 'PARTIALLY_OCCUPIED'
  | 'UNDER_MAINTENANCE' | 'BLOCKED'
export type BedStatus = 'AVAILABLE' | 'OCCUPIED' | 'BLOCKED'
export type ChargeRule = 'FREE' | 'FLAT_RATE' | 'METERED'
export type CouponType =
  | 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'TEA_SNACKS' | 'FULL_DAY' | 'CUSTOM'
export type PaymentFrequency = 'WEEKLY' | 'MONTHLY' | 'ATTENDANCE_BASED'
export type PaymentMode =
  | 'CASH' | 'PAYROLL_ALLOWANCE' | 'VOUCHER' | 'STORE_ISSUE' | 'DIGITAL_WALLET'

export interface Colony {
  id: string
  plant_id: string
  colony_code: string
  colony_name: string
  location: string | null
  ownership_type: OwnershipType
  total_rooms: number
  is_active: boolean
  remarks: string | null
}

export interface Room {
  id: string
  colony_id: string
  building_block: string | null
  floor: string | null
  room_no: string
  room_type: RoomType
  capacity: number
  allowed_gender: 'MALE' | 'FEMALE' | 'ANY' | null
  allowed_employee_category: string | null
  monthly_rent: string
  electricity_rule: ChargeRule
  electricity_flat_rate: string | null
  water_rule: 'FREE' | 'FLAT_RATE'
  water_flat_rate: string | null
  maintenance_rule: 'FREE' | 'FLAT_RATE'
  maintenance_flat_rate: string | null
  status: RoomStatus
  is_active: boolean
  remarks: string | null
}

export interface Bed {
  id: string
  room_id: string
  bed_no: string
  status: BedStatus
  remarks: string | null
}

export interface Canteen {
  id: string
  plant_id: string
  canteen_code: string
  canteen_name: string
  location: string | null
  is_active: boolean
  remarks: string | null
}

export interface CouponRule {
  id: string
  rule_code: string
  rule_name: string | null
  employee_category: string | null
  department_id: string | null
  shift_id: string | null
  canteen_id: string | null
  coupon_type: CouponType
  coupons_per_day: number
  coupons_per_week: number | null
  rate_per_coupon: string
  company_subsidy_percent: string
  employee_deduction_percent: string
  meal_window_start: string | null
  meal_window_end: string | null
  effective_from: string
  effective_to: string | null
  is_active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
  remarks: string | null
}

export interface RationRule {
  id: string
  rule_code: string
  rule_name: string | null
  employee_category: string | null
  employment_type: string | null
  accommodation_type: string | null
  department_id: string | null
  weekly_amount: string | null
  monthly_amount: string | null
  per_day_amount: string | null
  attendance_required: boolean
  minimum_present_days_per_week: number | null
  payable_if_on_leave: boolean
  payable_if_absent: boolean
  payment_frequency: PaymentFrequency
  payment_mode: PaymentMode
  week_start_day: number
  effective_from: string
  effective_to: string | null
  is_active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
  remarks: string | null
}

const BASE = '/welfare'

export const welfareMastersApi = {
  // Colonies
  listColonies: async (params: { plant_id?: string; is_active?: boolean } = {}) =>
    (await apiClient.get<Colony[]>(`${BASE}/colonies`, { params })).data,
  createColony: async (body: Record<string, unknown>) =>
    (await apiClient.post<Colony>(`${BASE}/colonies`, body)).data,
  patchColony: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<Colony>(`${BASE}/colonies/${id}`, body)).data,
  deleteColony: async (id: string) =>
    (await apiClient.delete(`${BASE}/colonies/${id}`)).data,

  // Rooms
  listRooms: async (params: { colony_id?: string; status?: string } = {}) =>
    (await apiClient.get<Room[]>(`${BASE}/rooms`, { params })).data,
  createRoom: async (body: Record<string, unknown>) =>
    (await apiClient.post<Room>(`${BASE}/rooms`, body)).data,
  patchRoom: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<Room>(`${BASE}/rooms/${id}`, body)).data,
  deleteRoom: async (id: string) =>
    (await apiClient.delete(`${BASE}/rooms/${id}`)).data,

  // Beds
  listBeds: async (params: { room_id?: string; status?: string } = {}) =>
    (await apiClient.get<Bed[]>(`${BASE}/beds`, { params })).data,
  createBed: async (body: { room_id: string; bed_no: string; remarks?: string | null }) =>
    (await apiClient.post<Bed>(`${BASE}/beds`, body)).data,
  patchBed: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<Bed>(`${BASE}/beds/${id}`, body)).data,
  deleteBed: async (id: string) =>
    (await apiClient.delete(`${BASE}/beds/${id}`)).data,

  // Canteens
  listCanteens: async (params: { plant_id?: string; is_active?: boolean } = {}) =>
    (await apiClient.get<Canteen[]>(`${BASE}/canteens`, { params })).data,
  createCanteen: async (body: Record<string, unknown>) =>
    (await apiClient.post<Canteen>(`${BASE}/canteens`, body)).data,
  patchCanteen: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<Canteen>(`${BASE}/canteens/${id}`, body)).data,

  // Coupon rules
  listCouponRules: async (params: { is_active?: boolean; employee_category?: string } = {}) =>
    (await apiClient.get<CouponRule[]>(`${BASE}/canteen-coupon-rules`, { params })).data,
  createCouponRule: async (body: Record<string, unknown>) =>
    (await apiClient.post<CouponRule>(`${BASE}/canteen-coupon-rules`, body)).data,
  patchCouponRule: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<CouponRule>(`${BASE}/canteen-coupon-rules/${id}`, body)).data,
  deleteCouponRule: async (id: string) =>
    (await apiClient.delete(`${BASE}/canteen-coupon-rules/${id}`)).data,

  // Ration rules
  listRationRules: async (params: { is_active?: boolean; employee_category?: string } = {}) =>
    (await apiClient.get<RationRule[]>(`${BASE}/ration-rules`, { params })).data,
  createRationRule: async (body: Record<string, unknown>) =>
    (await apiClient.post<RationRule>(`${BASE}/ration-rules`, body)).data,
  patchRationRule: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<RationRule>(`${BASE}/ration-rules/${id}`, body)).data,
  deleteRationRule: async (id: string) =>
    (await apiClient.delete(`${BASE}/ration-rules/${id}`)).data,
}
